import React, { useState, useEffect } from "react";
import {
  getFirestore, collection, collectionGroup, getDocs, doc, setDoc, updateDoc,
  query, where, orderBy, limit, serverTimestamp, getDoc, runTransaction
} from "firebase/firestore";
import { getApp, getApps, initializeApp } from "firebase/app";
import { useNavigate } from "react-router-dom";
import {
  getAuth, initializeAuth, inMemoryPersistence, signOut,
  createUserWithEmailAndPassword, updatePassword,
} from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./AdminRoles.css";
import "./AdminBulkTools.css";
import { publishToDirectory } from "../utils/educatorCode";
import AccountCheck from "../components/admin/AccountCheck";

// 4-letter word + 2 digits → exactly 6 chars (easy for 1st graders)
const PW_WORDS_4 = ["blue", "tree", "star", "frog", "bear", "lion", "book", "moon", "ship", "fish"];

function generateStudentPassword(i = 0) {
  const word = PW_WORDS_4[i % PW_WORDS_4.length]; // 4 letters
  const num = String(10 + ((i * 7) % 90));        // 10–99
  return `${word}${num}`;                          // length = 6
}
function makeEduCodePassword(educatorLastName = "", educatorCode = "") {
  const last = slugify(educatorLastName);        // lowercase, strip spaces/punct
  const code = String(educatorCode || "");       // ensure string
  let pw = `${last}${code}`;                     // no spaces
  while (pw.length < 6) pw += "1";               // Firebase min length
  return pw;
}
function slugify(s = "") {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Allocate the next sequential student number for an educator (1,2,3...)
async function allocateStudentSeq(db, educatorUid) {
  const ref = doc(db, "counters", `students_${educatorUid}`);
  const seq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      tx.set(ref, { next: 2 }); // first issued will be 1
      return 1;
    }
    const data = snap.data() || {};
    const next = typeof data.next === "number" ? data.next : 1;
    tx.update(ref, { next: next + 1 });
    return next;
  });
  return seq;
}
function isEducatorRole(role = "") {
  const r = String(role).toLowerCase();
  return r === "educator" || r.endsWith("educator") || r.endsWith("-educator");
}

// Reserve a block of sequential numbers in ONE update
async function allocateStudentSeqBlock(db, educatorUid, count) {
  const ref = doc(db, "counters", `students_${educatorUid}`);
  const start = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      // first time: next will be 1..count
      tx.set(ref, { next: 1 + count });
      return 1;
    }
    const data = snap.data() || {};
    const next = typeof data.next === "number" ? data.next : 1;
    tx.update(ref, { next: next + count });
    return next; // first number in this block
  });
  return { start, count };
}

// Allocate a unique educator code
async function allocateEducatorCode(db, step = 5, startAt = 471020) {
  const ref = doc(db, "counters", "educatorCode");
  const code = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      tx.set(ref, { next: startAt + step });
      return startAt;
    }
    const data = snap.data() || {};
    const current = typeof data.next === "number" ? data.next : startAt + step;
    tx.update(ref, { next: current + step });
    return current;
  });
  return code;
}

// Password: educator last name + student first name + seq, all lowercase, min 6 chars
function makeSeqPassword(educatorLastName = "", studentFirst = "", seq = 1) {
  let base = `${slugify(educatorLastName)}${slugify(studentFirst)}${seq}`;
  while (base.length < 6) base += "1"; // guarantee Firebase min length
  return base;
}

// Simple student email generator
function makeStudentEmail(studentFirst, studentLastInitial, educatorCode, seq) {
  const li = (studentLastInitial || "").trim();
  const base = `${slugify(studentFirst)}${li ? slugify(li) : ""}`.slice(0, 12) || "student";
  const num = String(seq).padStart(2, "0");
  return `${base}${num}-${educatorCode}@sbl.school`;
}

// Use a secondary app so creating users doesn't switch your admin session.
//
// Persistence is forced to in-memory. By default a named app writes its auth
// session to localStorage under its own key, so after creating one educator
// this app is holding THAT educator's session — and the next Save User starts
// by firing accounts:lookup to refresh a token that has since expired. That is
// the 400 (Bad Request) this line used to throw on every save after the first.
// In-memory means nothing is stored, so there is nothing stale to restore.
function getSecondaryAuth() {
  const primary = getApp();
  const name = "Secondary-App";
  const existing = getApps().find((a) => a.name === name);
  const secondary = existing || initializeApp(primary.options, name);

  let secondaryAuth;
  try {
    secondaryAuth = initializeAuth(secondary, { persistence: inMemoryPersistence });
  } catch {
    // initializeAuth throws if auth is already set up for this app. That is
    // the normal path on every call after the first one.
    secondaryAuth = getAuth(secondary);
  }
  return { secondary, secondaryAuth };
}

// Ensure the admin profile exists and is marked as an administrator
async function ensureAdminProfile(db, adminUid) {
  const adminRef = doc(db, "users", adminUid);
  const snap = await getDoc(adminRef);
  const currentRole = (snap.exists() ? (snap.data().role || '') : '').toLowerCase();

  if (!snap.exists() || !['admin','administrator'].includes(currentRole)) {
    await setDoc(
      adminRef,
      { role: "administrator", updatedAt: serverTimestamp() },
      { merge: true }
    );
    console.log("Admin profile ensured/updated to administrator.");
  } else {
    console.log("Admin profile exists and is administrator.");
  }

  // Verify (and handy log while you’re debugging)
  const verify = await getDoc(adminRef);
  console.log("Admin role now:", verify.data()?.role);
}

// Seed the educator code counter if it doesn’t exist
async function seedEducatorCodeCounter(db, startAt = 471020) {
  // Only try if rules will allow it.
  const meRef = doc(db, "users", getAuth().currentUser.uid);
  const meSnap = await getDoc(meRef);
  const isAdmin = ((meSnap.data()?.role || '').toLowerCase() === 'administrator' || (meSnap.data()?.role || '').toLowerCase() === 'admin');
  if (!isAdmin) return;

  const ref = doc(db, "counters", "educatorCode");
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, { next: startAt });
    console.log(`Educator code counter seeded at ${startAt}.`);
  }
}


// ---------------------------------------------------------------------------
// Bulk delete / filtering helpers
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

function roleKindOf(role = "") {
  const r = String(role).toLowerCase();
  if (r === "admin" || r === "administrator") return "admin";
  if (r.includes("educator")) return "educator";
  if (r.includes("student")) return "student";
  return "other";
}

/**
 * "Last seen" comes from three sources, best first:
 *
 *   1. users/{uid}.lastLoginAt  - a real login stamp, if recordLogin() is wired
 *                                 into your sign-in flow (see utils/recordLogin.js)
 *   2. Firebase Auth metadata   - lastSignInTime, via the adminListAuthUsers
 *                                 callable. True sign-in events only.
 *   3. users/{uid}/pageLogs.ts  - most recent page view. Reflects actual portal
 *                                 use, and survives even if the callable is down.
 *
 * Auth is authoritative for "did this login ever happen", but pageLogs is the
 * better signal for "is this account actually being used" — a token refresh
 * can bump Auth without anyone opening the portal.
 */
function toMs(value) {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

function resolveLastSeen(user, meta, activityMs) {
  const candidates = [
    { ms: toMs(user?.lastLoginAt), source: "login" },
    { ms: meta ? toMs(meta.lastSignInTime) : null, source: "auth" },
    { ms: activityMs ?? null, source: "activity" },
  ].filter((c) => c.ms != null);

  if (candidates.length === 0) {
    return { ms: null, source: null, hasAuthRecord: !!meta };
  }

  candidates.sort((a, b) => b.ms - a.ms);
  return { ...candidates[0], hasAuthRecord: !!meta };
}

function daysSince(ms) {
  if (ms == null) return null;
  return Math.floor((Date.now() - ms) / DAY_MS);
}

const SOURCE_LABEL = {
  login: "login",
  auth: "sign-in",
  activity: "page view",
};

function csvCell(value) {
  const s = String(value ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsvFile(filename, rows, headers) {
  const body = rows.map((r) => headers.map((h) => csvCell(r[h])).join(","));
  const blob = new Blob([[headers.join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const AdminRoles = () => {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ sirName: "", firstName: "", lastName: "", email: "", schoolName: "", schoolDistrict: "", state: "", grade: "", view: "", role: "", notes: "", password: "", educatorCode: "" });
  
 
  const [editUser, setEditUser] = useState(null);
  const [showAddUserPopup, setShowAddUserPopup] = useState(false);
  const [showDetailsPopup, setShowDetailsPopup] = useState(null);
  const [educatorCodeCounter, setEducatorCodeCounter] = useState(468024);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null }); // State for sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [studentsOfEducator, setStudentsOfEducator] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [createdStudentCreds, setCreatedStudentCreds] = useState([]);
  const [students, setStudents] = useState([]); // <-- add me
  const navigate = useNavigate();
  const db = getFirestore();
  const [canListUsers, setCanListUsers] = useState(false);

  // --- bulk delete / filtering state ---------------------------------------
  const [selectedIds, setSelectedIds] = useState([]);
  const [authMeta, setAuthMeta] = useState({});      // uid -> auth metadata
  const [activityMs, setActivityMs] = useState({});  // uid -> last pageLog ts
  const [activityLoading, setActivityLoading] = useState(false);
  const [authMetaLoading, setAuthMetaLoading] = useState(false);
  const [authMetaError, setAuthMetaError] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");   // all|educator|student|admin|other
  const [loginFilter, setLoginFilter] = useState("all"); // all|never|inactive30|inactive90|inactive180|active30
  const [stateFilter, setStateFilter] = useState("all");
  const [orphanFilter, setOrphanFilter] = useState(false); // Auth record but no profile
  const [busyMessage, setBusyMessage] = useState("");
  const [deleteReport, setDeleteReport] = useState(null);

   // 1) Hoist this to component scope
 const fetchData = async () => {
  try {
    const uid = getAuth(getApp()).currentUser?.uid;
    console.log("Listing /users as:", uid);
    const usersSnapshot = await getDocs(collection(db, "users"));
    const userList = usersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    setUsers(userList);
    loadActivity(userList);
  } catch (error) {
    console.error("Error fetching /users:", error);
  }
};

  // Pulls every Firebase Auth account, including ones with no Firestore
  // profile (the orphans that block re-registration with the same email).
  const loadAuthMetadata = async () => {
    setAuthMetaLoading(true);
    setAuthMetaError("");
    try {
      const fn = httpsCallable(getFunctions(), "adminListAuthUsers");
      const res = await fn({});
      const map = {};
      (res.data?.users || []).forEach((u) => {
        map[u.uid] = u;
      });
      setAuthMeta(map);
    } catch (err) {
      console.error("adminListAuthUsers failed:", err);
      setAuthMetaError(
        "Firebase Auth data unavailable — showing last activity from page logs " +
          "instead. Deploy adminListAuthUsers to see true sign-in times and to " +
          "find orphaned Auth logins."
      );
    } finally {
      setAuthMetaLoading(false);
    }
  };

  /**
   * Last portal activity, read straight from users/{uid}/pageLogs.
   * Tries one collectionGroup query first (cheap). If that needs a
   * collection-group index that doesn't exist yet, it falls back to one
   * tiny per-user query, run in small chunks.
   */
  const loadActivity = async (userList) => {
    const list = userList || users;
    if (!list.length) return;

    setActivityLoading(true);
    const map = {};

    try {
      const snap = await getDocs(
        query(collectionGroup(db, "pageLogs"), orderBy("ts", "desc"), limit(5000))
      );

      snap.docs.forEach((d) => {
        const uid = d.ref.parent.parent?.id;
        if (!uid) return;
        const ms = toMs(d.data()?.ts);
        if (ms == null) return;
        if (!map[uid] || ms > map[uid]) map[uid] = ms;
      });

      setActivityMs(map);
    } catch (err) {
      // Missing collection-group index, most likely. Fall back per user.
      console.warn("pageLogs group query failed, falling back per user:", err);

      const chunk = 20;
      for (let i = 0; i < list.length; i += chunk) {
        const slice = list.slice(i, i + chunk);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(
          slice.map(async (u) => {
            try {
              const s = await getDocs(
                query(
                  collection(db, "users", u.id, "pageLogs"),
                  orderBy("ts", "desc"),
                  limit(1)
                )
              );
              const ms = toMs(s.docs[0]?.data()?.ts);
              if (ms != null) map[u.id] = ms;
            } catch {
              /* no logs for this user */
            }
          })
        );
      }
      setActivityMs({ ...map });
    } finally {
      setActivityLoading(false);
    }
  };

  function formatDateMaybe(v) {
    if (!v) return "";
    if (v?.toDate) return v.toDate().toLocaleString();
    try { return new Date(v).toLocaleString(); } catch { return ""; }
  }

  useEffect(() => {
    // Wait for auth to be ready, then do admin bootstrap + fetch
   const app = getApp();
   const auth = getAuth(app);
   const unsub = onAuthStateChanged(auth, async (user) => {
     if (!user) {
       console.error("Admin is not signed in. Initialization skipped.");
       return;
     }
     await ensureAdminProfile(db, user.uid);      // guarantees role=administrator
     await seedEducatorCodeCounter(db);           // will now pass rules
     await fetchData();                           // collection read
     await loadAuthMetadata();                    // last-login data from Auth
   });
   return () => unsub();
  }, [db]);
  

  useEffect(() => {
    const fetchData = async () => {
      try {
        const uid = getAuth(getApp()).currentUser?.uid;
        console.log("Listing /users as:", uid);
        const usersSnapshot = await getDocs(collection(db, "users"));
        const userList = usersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsers(userList);
      } catch (error) {
        console.error("Error fetching /users:", error);
      }
    };

    fetchData();
  }, [db]);

  useEffect(() => {
    const run = async () => {
      if (!showDetailsPopup) return;
      const isEducator = (showDetailsPopup.role || "").toLowerCase().endsWith("educator");
      if (!isEducator) {
        setStudentsOfEducator([]);
        return;
      }
      setLoadingStudents(true);
      try {
        const usersRef = collection(db, "users");

        let q;
        if (showDetailsPopup.id) {
          q = query(usersRef, where("educatorUid", "==", showDetailsPopup.id));
        } else if (showDetailsPopup.educatorCode) {
          q = query(usersRef, where("educatorCode", "==", showDetailsPopup.educatorCode));
        }

        if (!q) {
          setStudentsOfEducator([]);
          setLoadingStudents(false);
          return;
        }

        const snap = await getDocs(q);
        const rows = await Promise.all(
          snap.docs.map(async (d) => {
            const s = d.data();
            let tempPassword = "";
            try {
              const priv = await getDoc(doc(db, "usersPrivate", d.id));
              if (priv.exists()) {
                const p = priv.data();
                tempPassword = p?.tempPassword || "";
              }
            } catch {}
            return {
              id: d.id,
              name: `${s.firstName || ""} ${s.lastName ? s.lastName[0] + "." : ""}`.trim(),
              email: s.email || "",
              tempPassword,
              createdAt: s.createdAt || s.joinDate || null,
              studentNumber: s.studentNumber || null,
            };
          })
        );

        // Sort by studentNumber first, then by createdAt
        rows.sort((a, b) => {
          const an = a.studentNumber ?? Number.MAX_SAFE_INTEGER;
          const bn = b.studentNumber ?? Number.MAX_SAFE_INTEGER;
          if (an !== bn) return an - bn;
          const at = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
          const bt = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
          return at - bt;
        });

        // Attach display label
        const labeled = rows.map((r, i) => ({
          ...r,
          label: `Student ${r.studentNumber ?? (i + 1)}`,
        }));

        setStudentsOfEducator(labeled);
      } finally {
        setLoadingStudents(false);
      }
    };

    run();
  }, [showDetailsPopup, db]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedUsers = [...users].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setUsers(sortedUsers);
  };

  const handleAddUser = async () => {
    // State, grade and view exist to build a scoped role string
    // ("va-3rd-educator") and to route a person to their own portal page. An
    // administrator has neither — App.js lists "administrator" in allowedRoles
    // on every route, so an admin sees everything regardless of what these
    // say. Requiring them means inventing two facts that are then stored and
    // meaningless.
    const roleIsAdmin = ["admin", "administrator"].includes(
      (newUser.role || "").toLowerCase()
    );

    if (
      !newUser.sirName ||
      !newUser.firstName ||
      !newUser.email ||
      !newUser.role ||
      !newUser.password ||
      (!roleIsAdmin && (!newUser.state || !newUser.grade || !newUser.view))
    ) {
      alert("Please fill in all mandatory fields.");
      return;
    }

    // Firebase lowercases the email itself but keeps the password byte for
    // byte, so a pasted trailing space becomes part of the password forever
    // and can never be typed back in at sign-in. Both sides of the login have
    // to agree, so containers/navbar/login.js trims the same way.
    const email = (newUser.email || "").trim().toLowerCase();
    const password = (newUser.password || "").trim();
  
    try {
      const adminAuth = getAuth();
      const adminUid = adminAuth.currentUser?.uid || null;
      const { secondary, secondaryAuth } = getSecondaryAuth();
  
      // 1) Create EDUCATOR
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        email,
        password
      );
      const educatorUid = userCredential.user.uid;
      const userRef = doc(db, "users", educatorUid);
  
      const roleIsEducator = (newUser.role || "").toLowerCase().endsWith("-educator");
      const educatorCode = roleIsEducator
        ? String(await allocateEducatorCode(db, 5, 471020))
        : "";
  
        const newUserData = {
          sirName: newUser.sirName,
          firstName: newUser.firstName,
          lastName: newUser.lastName || "",
          email,
          schoolName: newUser.schoolName || "",
          schoolDistrict: newUser.schoolDistrict || "",
          state: newUser.state,
          grade: newUser.grade,
          view: newUser.view,
          role: newUser.role,
          notes: newUser.notes || "",
          educatorCode,
          joinDate: new Date().toISOString(),
          createdAt: serverTimestamp(),
          createdBy: adminUid,
        
          /** ⭐ DEFAULT AVATAR ASSIGNED ON ACCOUNT CREATION */
          avatarUrl:
            "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/avatars%2Favatar24.png?alt=media&token=4ff5f59e-fd2f-4950-b984-f7f849f7b291",
        };
        

      await setDoc(userRef, newUserData);
      setUsers((prev) => [...prev, { id: educatorUid, ...newUserData }]);
      // List the new educator's code in the directory, so students can find
      // it at sign-up straight away (admins may write it — firestore.rules).
      if (educatorCode) await publishToDirectory(educatorUid, educatorCode, newUserData);
  
      // 2) Students (only after educator created)
     // 2) Students (only after educator created)
if (isEducatorRole(newUser.role) && Array.isArray(students) && students.length > 0) {
  const validStudents = students.filter(
    (s) => (s.firstName || "").trim()
  );

  if (validStudents.length === 0) {
    console.log("No valid students to create.");
  } else {
    let start;
    try {
      const block = await allocateStudentSeqBlock(db, educatorUid, validStudents.length);
      start = block.start;
    } catch (e) {
      console.error("allocateStudentSeqBlock failed:", e);
      // Fallback: allocate one-by-one so we don't abort the whole run
      start = null;
    }

    const createdCreds = [];
    const createdStudentDocs = [];
    const failures = [];
    let idx = 0;

    for (const st of validStudents) {
      const first = (st.firstName || "").trim();
      const lastI = (st.lastInitial || "").trim();

      try {
        const studentNumber = start != null ? (start + idx) : await allocateStudentSeq(db, educatorUid);
        idx += 1;

        // safer email for duplicates during testing
        const email = makeStudentEmail(first, lastI, educatorCode, studentNumber);
        const password = makeEduCodePassword(newUser.lastName || "teacher", educatorCode);


        const stCred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
        const stUid = stCred.user.uid;

        const studentDoc = {
          sirName: "N/A",
          firstName: first,
          lastName: lastI,
          email,
          state: newUser.state,
          grade: newUser.grade,
          view: "Student",
          role: (newUser.role || "").toLowerCase().replace(/-educator$/, "-student"),
          schoolName: newUser.schoolName || "",
          schoolDistrict: newUser.schoolDistrict || "",
          notes: `Auto-created under educator ${newUser.firstName} ${newUser.lastName || ""}`,
          educatorCode,
          educatorUid,
          studentNumber,
          createdAt: serverTimestamp(),
          createdBy: adminUid,
        
          /** ⭐ DEFAULT AVATAR FOR ALL STUDENTS TOO */
          avatarUrl:
            "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/avatars%2Favatar24.png?alt=media&token=4ff5f59e-fd2f-4950-b984-f7f849f7b291",
        };
        
        await setDoc(doc(db, "users", stUid), studentDoc);

        try {
          await setDoc(doc(db, "usersPrivate", stUid), {
            tempPassword: password,
            educatorUid,
            createdAt: serverTimestamp(),
          });
        } catch (e) {
          console.warn(`[usersPrivate/${stUid}] write failed`, e);
        }

        createdCreds.push({
          label: `Student ${studentNumber}`,
          name: `${first}${lastI ? ` ${lastI}.` : ""}`,
          email,
          password,
          createdAt: new Date(),
        });
        createdStudentDocs.push({ id: stUid, ...studentDoc });
      } catch (e) {
        console.error(`Student "${first} ${lastI}" failed:`, e);
        // log the exact Firebase error code for quick triage
        failures.push({ first, lastI, code: e?.code, error: e?.message || String(e) });
      }
    }

    setCreatedStudentCreds(createdCreds);
    setUsers((prev) => [...prev, ...createdStudentDocs]);

    if (failures.length) {
      alert(
        `Some students could not be added:\n` +
        failures.map((f) => `- ${f.first} ${f.lastI}: ${f.code || ""} ${f.error || ""}`).join("\n")
      );
    }
  }
}

  
      // Clear forms only AFTER student creation finishes.
      // schoolName and schoolDistrict were missing from this reset, so they
      // carried over silently into the next user added.
      setNewUser({
        sirName: "", firstName: "", lastName: "", email: "", schoolName: "",
        schoolDistrict: "", state: "", grade: "", view: "", role: "",
        notes: "", password: "", educatorCode: "",
      });
      setStudents([]);

      // Closed here rather than in `finally`: if the save throws, the popup
      // should stay open with everything still typed into it.
      setShowAddUserPopup(false);
  
    } catch (error) {
      console.error("Error adding user:", error?.message || error);
      alert(`Failed to add user(s): ${error?.message || error}`);
    } finally {
      // Sign the secondary app out rather than deleting it. deleteApp leaves a
      // disposed app that getApps() can still return, and the next call then
      // builds an auth instance on a dead app — the other half of the 400.
      try {
        const { secondaryAuth } = getSecondaryAuth();
        await signOut(secondaryAuth);
      } catch {}
    }
  };
  
  

  const handleEditUser = async () => {
    if (!editUser) return;

    try {
        console.log("Editing user:", editUser);

        const userRef = doc(db, "users", editUser.id);

        await updateDoc(userRef, {
            sirName: editUser.sirName || "",
            firstName: editUser.firstName || "",
            lastName: editUser.lastName || "",
            email: editUser.email || "",
            schoolName: editUser.schoolName || "",
schoolDistrict: editUser.schoolDistrict || "",
            state: editUser.state || "",
            grade: editUser.grade || "",
            view: editUser.view || "",
            role: editUser.role || "",
            notes: editUser.notes || "",
            educatorCode: editUser.educatorCode || "",
            // keep "signed up as" in step with the role, or the pages that
            // read it would still treat a re-labelled student as a teacher
            ...(/student$/i.test(editUser.role || "") ? { intendedRole: "student" } : {}),
            ...(/educator$/i.test(editUser.role || "") ? { intendedRole: "educator" } : {}),
        });

        // Password update (only if a new password is provided)
        if (editUser.password && editUser.password.trim() !== "") {
            const auth = getAuth();
            const user = auth.currentUser;

            if (user && (user.uid === editUser.id || user.role === "administrator")) {
                await updatePassword(user, editUser.password);
                console.log("Password updated successfully!");
            } else {
                console.warn("Cannot update password: User not logged in or incorrect permissions.");
            }
        }

        setUsers((prev) =>
            prev.map((user) => (user.id === editUser.id ? { ...editUser, password: "" } : user)) // Clear password field after update
        );

        setEditUser(null);
        alert("User updated successfully!");
    } catch (error) {
        console.error("Error updating user:", error.message);
        alert("Failed to update user. Please check permissions in Firestore.");
    }
};

// ---------------------------------------------------------------------------
// DELETION — routed through the adminDeleteUsers Cloud Function so the
// Firebase Auth account goes with the Firestore profile. Deleting the profile
// alone is what leaves an orphaned email that blocks re-registration.
// ---------------------------------------------------------------------------

// Why this is chunked: one callable carrying all 45 uids ran past the
// 60-second deadline, so the client got "deadline-exceeded" after 39 accounts
// were already gone and the other 6 never ran. Small batches keep every call
// well under the limit, and a failure in one batch no longer throws away the
// results of the batches that already finished.
//
// Keep this small. includeStudents: true means one educator in a batch also
// drags their whole roster into that same call on the server.
const DELETE_BATCH_SIZE = 5;
const PREVIEW_BATCH_SIZE = 25;
// Client-side ceiling for a single call (9 min, the gen-2 callable max).
const DELETE_CALL_TIMEOUT_MS = 540000;

const emptyResultRow = (uid, error) => ({
  uid,
  email: users.find((u) => u.id === uid)?.email || "",
  role: users.find((u) => u.id === uid)?.role || "",
  authFound: false,
  authDeleted: false,
  firestoreDocsFound: [],
  firestoreDocsDeleted: 0,
  storageFilesDeleted: 0,
  backedUp: false,
  status: "ERROR",
  error,
});

const applyDeletedToState = (goneIds) => {
  if (goneIds.length === 0) return;
  const gone = new Set(goneIds);

  setUsers((prev) => prev.filter((u) => !gone.has(u.id)));
  setSelectedIds((prev) => prev.filter((id) => !gone.has(id)));
  setShowDetailsPopup((prev) => (prev && gone.has(prev.id) ? null : prev));

  // Auth metadata is now stale for the deleted accounts.
  setAuthMeta((prev) => {
    const next = { ...prev };
    goneIds.forEach((id) => delete next[id]);
    return next;
  });
};

const runDeletion = async ({ uids, dryRun }) => {
  const fn = httpsCallable(getFunctions(), "adminDeleteUsers", {
    timeout: DELETE_CALL_TIMEOUT_MS,
  });

  const size = dryRun ? PREVIEW_BATCH_SIZE : DELETE_BATCH_SIZE;
  const batches = [];
  for (let i = 0; i < uids.length; i += size) {
    batches.push(uids.slice(i, i + size));
  }

  // uid -> result row. A student pulled in by an educator in one batch can
  // also be selected in a later batch; keep the row that says it was deleted.
  const byUid = new Map();
  const skipped = [];
  let processed = 0;
  let failedBatches = 0;
  let doneCount = 0;

  const mergeRow = (row) => {
    const existing = byUid.get(row.uid);
    if (!existing || !existing.status?.startsWith("DELETED")) {
      byUid.set(row.uid, row);
    }
  };

  try {
    for (let b = 0; b < batches.length; b++) {
      const batch = batches[b];

      setBusyMessage(
        `${dryRun ? "Previewing" : "Deleting"} batch ${b + 1} of ${batches.length} ` +
          `— ${doneCount} of ${uids.length} selected account(s) done…`
      );

      try {
        const res = await fn({
          uids: batch,
          dryRun,
          includeStudents: true,
          backupEducators: true,
        });

        const report = res.data || {};
        const rows = report.results || [];
        rows.forEach(mergeRow);
        (report.skipped || []).forEach((s) => skipped.push(s));
        processed += report.processed ?? rows.length;

        if (!dryRun) {
          applyDeletedToState(
            rows.filter((r) => r.status?.startsWith("DELETED")).map((r) => r.uid)
          );
        }
      } catch (error) {
        failedBatches++;
        console.error(`adminDeleteUsers batch ${b + 1} failed:`, error);

        // The server may still have finished some of this batch after the
        // client gave up. Mark them as errors; a page refresh shows the truth,
        // and re-running the delete on anything left is safe.
        batch.forEach((uid) => {
          if (!byUid.has(uid)) {
            mergeRow(
              emptyResultRow(
                uid,
                `${error.code || "error"}: ${error.message} (may have partly ` +
                  `completed — refresh and re-run on anything still listed)`
              )
            );
          }
        });

        if (error.code === "functions/not-found") {
          alert(
            "adminDeleteUsers was not found. Deploy it first:\n" +
              "firebase deploy --only functions:adminDeleteUsers"
          );
          break;
        }
      }

      doneCount += batch.length;
    }

    const merged = {
      dryRun,
      requested: uids.length,
      processed,
      skipped,
      results: Array.from(byUid.values()),
      failedBatches,
    };

    setDeleteReport(merged);
    return merged;
  } finally {
    setBusyMessage("");
  }
};

const handleDeleteUser = async (userId) => {
  const user = users.find((u) => u.id === userId);

  const students = users.filter(
    (u) =>
      roleKindOf(u.role) === "student" &&
      (u.educatorUid === userId ||
        (user?.educatorCode && u.educatorCode === user.educatorCode))
  );

  const confirmDelete = window.confirm(
    `Permanently delete this account everywhere?\n\n` +
      `Name: ${user?.firstName || ""} ${user?.lastName || ""}\n` +
      `Email: ${user?.email || ""}\n\n` +
      (students.length
        ? `⚠️ ${students.length} student account(s) under this educator will also be deleted.\n\n`
        : "") +
      `This removes: Firebase Auth login, the /users profile, progress, ` +
      `leaderboard entries, purchase orders, roster rows and Storage files.\n\n` +
      `Any Stripe subscription they pay for is cancelled right away (no more charges).\n\n` +
      (roleKindOf(user?.role) === "educator"
        ? `A backup of the educator's details is saved to the Email Directory first.\n\n`
        : "") +
      `This cannot be undone.`
  );

  if (!confirmDelete) return;

  const report = await runDeletion({ uids: [userId], dryRun: false });
  if (report) {
    const ok = report.results.filter((r) => r.status.startsWith("DELETED")).length;
    alert(`Deleted ${ok} account(s). See the report for details.`);
  }
};

const toggleSelect = (userId) => {
  setSelectedIds((prev) =>
    prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
  );
};

const handlePreviewBulkDelete = async () => {
  if (selectedIds.length === 0) return;
  await runDeletion({ uids: selectedIds, dryRun: true });
};

const handleBulkDeleteEverywhere = async () => {
  if (selectedIds.length === 0) return;

  const chosen = users.filter((u) => selectedIds.includes(u.id));
  const educators = chosen.filter((u) => roleKindOf(u.role) === "educator");
  const studentsIncluded = users.filter(
    (u) =>
      roleKindOf(u.role) === "student" &&
      !selectedIds.includes(u.id) &&
      educators.some(
        (e) =>
          u.educatorUid === e.id ||
          (e.educatorCode && u.educatorCode === e.educatorCode)
      )
  );

  const total = selectedIds.length + studentsIncluded.length;

  const first = window.confirm(
    `Permanently delete ${selectedIds.length} selected account(s)?\n\n` +
      `Educators: ${educators.length}\n` +
      `Students already selected: ${
        chosen.filter((u) => roleKindOf(u.role) === "student").length
      }\n` +
      (studentsIncluded.length
        ? `Students pulled in under those educators: ${studentsIncluded.length}\n`
        : "") +
      `\nTOTAL ACCOUNTS TO DELETE: ${total}\n\n` +
      `Each one loses its Firebase Auth login, Firestore documents and ` +
      `Storage files. Educator details are backed up first; student and ` +
      `child records are not backed up.`
  );
  if (!first) return;

  const typed = window.prompt(
    `Final confirmation.\n\nType DELETE ${total} to proceed.`
  );
  if (typed !== `DELETE ${total}`) {
    alert("Confirmation did not match. Nothing was deleted.");
    return;
  }

  const report = await runDeletion({ uids: selectedIds, dryRun: false });
  if (report) {
    const ok = report.results.filter((r) => r.status.startsWith("DELETED")).length;
    const failed = report.results.filter((r) => r.status === "ERROR").length;
    alert(
      `Deleted ${ok} account(s).` +
        (failed ? `\n${failed} failed — see the report.` : "") +
        (report.failedBatches
          ? `\n\n${report.failedBatches} batch(es) timed out or errored. ` +
            `Refresh the page and re-run the delete on anything still listed.`
          : "")
    );
  }
};

const handleExportSelectedEducators = () => {
  const rows = users
    .filter((u) => selectedIds.includes(u.id) && roleKindOf(u.role) === "educator")
    .map((u) => {
      const seen = resolveLastSeen(u, authMeta[u.id], activityMs[u.id]);
      return {
        email: u.email || "",
        sirName: u.sirName || "",
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        schoolName: u.schoolName || "",
        schoolDistrict: u.schoolDistrict || "",
        state: u.state || "",
        grade: u.grade || "",
        role: u.role || "",
        educatorCode: u.educatorCode || "",
        uid: u.id,
        lastSeen: seen.ms ? new Date(seen.ms).toISOString() : "never",
        lastSeenSource: seen.source ? SOURCE_LABEL[seen.source] : "",
        notes: u.notes || "",
      };
    });

  if (rows.length === 0) {
    alert("No educators are selected.");
    return;
  }

  downloadCsvFile(
    `sbl-educators-${new Date().toISOString().slice(0, 10)}.csv`,
    rows,
    [
      "email", "sirName", "firstName", "lastName", "schoolName",
      "schoolDistrict", "state", "grade", "role", "educatorCode",
      "uid", "lastSeen", "lastSeenSource", "notes",
    ]
  );
};

  // Auth accounts with no matching Firestore profile — these are exactly the
  // leftovers that stop someone re-registering with their old email.
  const orphanAuthAccounts = React.useMemo(() => {
    const profileIds = new Set(users.map((u) => u.id));
    return Object.values(authMeta).filter((a) => !profileIds.has(a.uid));
  }, [authMeta, users]);

  const availableStates = React.useMemo(() => {
    const set = new Set(users.map((u) => u.state).filter(Boolean));
    return [...set].sort();
  }, [users]);

  const filteredRoleUsers = React.useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    // Orphan mode shows Auth-only accounts shaped like user rows.
    if (orphanFilter) {
      return orphanAuthAccounts
        .map((a) => ({
          id: a.uid,
          email: a.email,
          firstName: a.displayName || "(no profile)",
          lastName: "",
          role: "",
          state: "",
          grade: "",
          view: "",
          educatorCode: "",
          schoolName: "",
          schoolDistrict: "",
          _orphan: true,
        }))
        .filter((u) => !q || (u.email || "").toLowerCase().includes(q));
    }

    return users.filter((u) => {
      if (q) {
        // Every term must match somewhere, so "smith john", "john smith"
        // and "jo sm" all find the same person. Email is matched whole and
        // by its local part, so "jsmith" finds jsmith@school.org.
        const email = String(u.email || "").toLowerCase();
        const haystack = [
          u.firstName, u.lastName,
          `${u.firstName || ""} ${u.lastName || ""}`,
          `${u.lastName || ""} ${u.firstName || ""}`,
          email, email.split("@")[0], email.split("@")[1],
          u.role, u.state, u.grade, u.view, u.educatorCode,
          u.schoolName, u.schoolDistrict, u.id,
        ].filter(Boolean).join(" ").toLowerCase();

        const terms = q.split(/\s+/).filter(Boolean);
        if (!terms.every((t) => haystack.includes(t))) return false;
      }

      if (roleFilter !== "all" && roleKindOf(u.role) !== roleFilter) return false;

      if (stateFilter !== "all" && (u.state || "") !== stateFilter) return false;

      if (loginFilter !== "all") {
        const seen = resolveLastSeen(u, authMeta[u.id], activityMs[u.id]);
        const ms = seen.ms;
        const days = daysSince(ms);

        if (loginFilter === "never" && ms !== null) return false;
        if (loginFilter === "active30" && (ms === null || days > 30)) return false;
        if (loginFilter === "inactive30" && !(ms === null || days > 30)) return false;
        if (loginFilter === "inactive90" && !(ms === null || days > 90)) return false;
        if (loginFilter === "inactive180" && !(ms === null || days > 180)) return false;
      }

      return true;
    });
  }, [
    users, searchTerm, roleFilter, stateFilter, loginFilter,
    authMeta, activityMs, orphanFilter, orphanAuthAccounts,
  ]);

  const allFilteredSelected =
    filteredRoleUsers.length > 0 &&
    filteredRoleUsers.every((u) => selectedIds.includes(u.id));

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const ids = new Set(filteredRoleUsers.map((u) => u.id));
      setSelectedIds((prev) => prev.filter((id) => !ids.has(id)));
    } else {
      setSelectedIds((prev) => [
        ...new Set([...prev, ...filteredRoleUsers.map((u) => u.id)]),
      ]);
    }
  };


  return (
    <div className="container">
      <button onClick={() => navigate("/admin")} className="return-button">
        Return to Admin Dashboard
      </button>
      <h1>Admin Dashboard: Manage Users</h1>

      <AccountCheck />

      {/* User Management Section */}
      <div className="section">
        <h2>User Management</h2>
        <button onClick={() => setShowAddUserPopup(true)} className="add-button">
          Add New User
        </button>

        <div className="admin-search-wrapper">
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search first name, last name, email, school, role, state, or educator code…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <span className="admin-search-count">
              {filteredRoleUsers.length} of {users.length} users
            </span>
          )}
        </div>

        {/* ---------------- FILTERS + BULK ACTIONS ---------------- */}
        <div className="abt-panel">
          {authMetaError && <div className="abt-warning">{authMetaError}</div>}

          <div className="abt-filters">
            <label className="abt-field">
              <span>Account type</span>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="all">All accounts</option>
                <option value="educator">Educators only</option>
                <option value="student">Students / children only</option>
                <option value="admin">Administrators</option>
                <option value="other">Unassigned role</option>
              </select>
            </label>

            <label className="abt-field">
              <span>Last seen</span>
              <select value={loginFilter} onChange={(e) => setLoginFilter(e.target.value)}>
                <option value="all">Any</option>
                <option value="never">Never logged in</option>
                <option value="inactive30">No login in 30+ days</option>
                <option value="inactive90">No login in 90+ days</option>
                <option value="inactive180">No login in 180+ days</option>
                <option value="active30">Active in last 30 days</option>
              </select>
            </label>

            <label className="abt-field">
              <span>State</span>
              <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                <option value="all">All states</option>
                {availableStates.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>

            <label className="abt-toggle">
              <input
                type="checkbox"
                checked={orphanFilter}
                onChange={(e) => {
                  setOrphanFilter(e.target.checked);
                  setSelectedIds([]);
                }}
              />
              <span>
                Orphaned Auth logins ({orphanAuthAccounts.length})
              </span>
            </label>

            <button
              type="button"
              className="abt-btn"
              onClick={() => {
                loadAuthMetadata();
                loadActivity();
              }}
              disabled={authMetaLoading || activityLoading}
            >
              {authMetaLoading || activityLoading
                ? "Loading activity…"
                : "Refresh activity data"}
            </button>
          </div>

          {orphanFilter && (
            <p className="abt-hint">
              These Firebase Auth logins have no profile in <code>/users</code>.
              They are what blocks someone from signing up again with the same
              email. Select and delete them to free the address.
            </p>
          )}

          <div className="abt-actions">
            <button type="button" className="abt-btn" onClick={toggleSelectAllFiltered}>
              {allFilteredSelected ? "Clear selection" : `Select all ${filteredRoleUsers.length} shown`}
            </button>

            <span className="abt-count">
              {selectedIds.length} selected
            </span>

            <button
              type="button"
              className="abt-btn"
              onClick={() => setSelectedIds([])}
              disabled={selectedIds.length === 0}
            >
              Deselect all
            </button>

            <button
              type="button"
              className="abt-btn abt-btn-blue"
              onClick={handleExportSelectedEducators}
              disabled={selectedIds.length === 0}
            >
              Back up selected educators (CSV)
            </button>

            <button
              type="button"
              className="abt-btn abt-btn-amber"
              onClick={handlePreviewBulkDelete}
              disabled={selectedIds.length === 0 || !!busyMessage}
            >
              Preview deletion
            </button>

            <button
              type="button"
              className="abt-btn abt-btn-red"
              onClick={handleBulkDeleteEverywhere}
              disabled={selectedIds.length === 0 || !!busyMessage}
            >
              Delete selected everywhere
            </button>

            <button
              type="button"
              className="abt-btn"
              onClick={() => navigate("/admin/emails")}
            >
              Email Directory →
            </button>
          </div>

          {busyMessage && <div className="abt-busy">{busyMessage}</div>}
        </div>

        <div className="bordered-container">
          <table className="excel-table">
            <thead>
              <tr>
                <th className="abt-check-col">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAllFiltered}
                    title="Select everything currently shown"
                  />
                </th>
                <th>Last Seen</th>
                <th>UID</th> {/* New UID column */}
                {["firstName", "lastName", "state", "grade", "view", "role", "educatorCode"].map((key) => (
                  <th key={key}>
                    <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                    <button
                      type="button"
                      className="sort-button"
                      onClick={() => handleSort(key)}
                      style={{ marginLeft: 6 }}
                    >
                      {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : "⇅"}
                    </button>
                  </th>
                ))}
                <th>School Name</th>
                <th>School District</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoleUsers.map((user) => (
                <tr
                  key={user.id}
                  className={selectedIds.includes(user.id) ? "abt-row-selected" : ""}
                >
                  <td className="abt-check-col">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(user.id)}
                      onChange={() => toggleSelect(user.id)}
                    />
                  </td>
                  <td className="abt-login-cell">
                    {(() => {
                      const seen = resolveLastSeen(
                        user,
                        authMeta[user.id],
                        activityMs[user.id]
                      );

                      if (seen.ms == null) {
                        return (
                          <span className="abt-pill abt-pill-grey">
                            {seen.hasAuthRecord || Object.keys(authMeta).length === 0
                              ? "Never"
                              : "No Auth record"}
                          </span>
                        );
                      }

                      return (
                        <>
                          {new Date(seen.ms).toLocaleDateString()}
                          <span className="abt-sub">
                            {daysSince(seen.ms)}d ago · {SOURCE_LABEL[seen.source]}
                          </span>
                        </>
                      );
                    })()}
                  </td>
                  <td>{user.id}</td> {/* Display the UID */}
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td>{user.state}</td>
                  <td>{user.grade}</td>
                  <td>{user.view}</td>
                  <td>{user.role}</td>
                  <td>{user.educatorCode}</td>
                  <td>{user.schoolName}</td>
<td>{user.schoolDistrict}</td>
                  <td>{user.email}</td>
                  <td>
                    {!user._orphan && (
                      <button
                        onClick={() => setShowDetailsPopup(user)}
                        className="details-button"
                      >
                        Details
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="abt-btn abt-btn-red abt-btn-sm"
                      disabled={!!busyMessage}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Popup */}{showAddUserPopup && (
  <div className="fullscreen-modal">
  <div className="popup-3d">
    <div className="popup-header">
      <div className="header-icon">👤</div>
      <div className="header-text">
        <h2>Add New User</h2>
        <p>Fill in the details below. Fields with * are required.</p>
      </div>
    </div>

    <div className="popup-content">
      {/* Group: Identity */}
      <div className="field-card">
        <div className="card-title">Identity</div>
        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Sir Name *</label>
            <select
              value={newUser.sirName}
              onChange={(e) => setNewUser({ ...newUser, sirName: e.target.value })}
            >
              <option value="">Choose one</option>
              {["N/A", "Mr.", "Mrs.", "Ms.", "Miss", "Principal", "Vice Principal", "Assistant Principal"].map((sirName) => (
                <option key={sirName} value={sirName}>{sirName}</option>
              ))}
            </select>
            <small className="hint">
  Student password = educator last name + educator code (no spaces, lowercase). Example: <code>johnson471020</code>
</small>

          </div>

          <div className="form-field">
            <label>First Name *</label>
            <input
              type="text"
              placeholder="e.g. Alex"
              value={newUser.firstName}
              onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
            />
          </div>

          <div className="form-field">
            <label>Last Name</label>
            <input
              type="text"
              placeholder="e.g. Johnson"
              value={newUser.lastName}
              onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Group: Contact & Login */}
      <div className="field-card">
        <div className="card-title">Contact & Login</div>
        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Email *</label>
            <input
              type="email"
              // Browsers autofill the admin's own saved login into this pair of
              // fields, so the account gets created with credentials nobody
              // typed — it looks fine in the console and then rejects the
              // password at first sign-in.
              autoComplete="off"
              placeholder="name@example.com"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
            <small className="hint">We’ll use this for sign-in.</small>
          </div>

          <div className="form-field">
            <label>Password *</label>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="Create a password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            />
            <small className="hint">At least 8 characters is best.</small>
          </div>
        </div>
      </div>

      {/* Group: School Setup */}
      <div className="field-card">
        <div className="card-title">School Setup</div>
        {/* INSERT: School Name + School District (above State/Grade/View) */}
<div className="form-grid cols-2">
  <div className="form-field">
    <label>School Name *</label>
    <input
      type="text"
      placeholder="e.g., Jefferson Elementary"
      value={newUser.schoolName || ""}
      onChange={(e) => setNewUser({ ...newUser, schoolName: e.target.value })}
    />
  </div>

  <div className="form-field">
    <label>School District *</label>
    <input
      type="text"
      placeholder="e.g., Loudoun County Public Schools"
      value={newUser.schoolDistrict || ""}
      onChange={(e) => setNewUser({ ...newUser, schoolDistrict: e.target.value })}
    />
  </div>
</div>

        <div className="form-grid cols-3">
          <div className="form-field">
            <label>State *</label>
            <select
              value={newUser.state}
              onChange={(e) => setNewUser({ ...newUser, state: e.target.value })}
            >
              <option value="">Choose state</option>
              {[
                "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
                "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
                "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
                "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
                "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"
              ].map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>Grade *</label>
            <select
              value={newUser.grade}
              onChange={(e) => setNewUser({ ...newUser, grade: e.target.value })}
            >
              <option value="">Choose grade</option>
              {["1st","2nd","3rd","4th","5th","6th","7th","8th","9th","10th","11th","12th","14th","15th","16th",].map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>View *</label>
            <select
              value={newUser.view}
              onChange={(e) => setNewUser({ ...newUser, view: e.target.value })}
            >
              <option value="">Choose role view</option>
              {["Educator","Student"].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Role *</label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              <option value="">Choose role code</option>
              {(() => {
                const states = [
                  "al","ak","az","ar","ca","co","ct","dc","de","fl","ga",
                  "hi","id","il","in","ia","ks","ky","la","me","md",
                  "ma","mi","mn","ms","mo","mt","ne","nv","nh","nj",
                  "nm","ny","nc","nd","oh","ok","or","pa","ri","sc",
                  "sd","tn","tx","ut","vt","va","wa","wv","wi","wy"
                ];
                const grades = ["1st","2nd","3rd","4th","5th","6th","7th","8th","9th","10th","11th","12th","14th","15th","16th"];
                const roles = ["educator","student"];
                const allRoles = ["administrator"];
                states.forEach(s => grades.forEach(g => roles.forEach(r => allRoles.push(`${s}-${g}-${r}`))));
                return allRoles.map((r) => <option key={r} value={r}>{r}</option>);
              })()}
            </select>
            <small className="hint">Example: <code>nc-3rd-educator</code></small>
          </div>

          <div className="form-field">
            <label>Educator Code</label>
            <input
              type="text"
              placeholder="Auto/Manual if educator"
              value={newUser.educatorCode}
              onChange={(e) => setNewUser({ ...newUser, educatorCode: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Group: Students (optional) */}
      <div className="field-card">
        <div className="card-title">Students (optional)</div>

        {(!Array.isArray(students) || students.length === 0) && (
          <small className="hint">Add students under this educator (first name + last initial).</small>
        )}

        {Array.isArray(students) && students.map((st, idx) => (
          <div key={idx} className="form-grid cols-3">
            <div className="form-field">
              <label>Student First Name</label>
              <input
                type="text"
                value={st.firstName || ""}
                onChange={(e) => {
                  const copy = [...students];
                  copy[idx] = { ...copy[idx], firstName: e.target.value };
                  setStudents(copy);
                }}
              />
            </div>

            <div className="form-field">
              <label>Student Last Initial</label>
              <input
                type="text"
                value={st.lastInitial || ""}
                maxLength={1}
                onChange={(e) => {
                  const copy = [...students];
                  copy[idx] = { ...copy[idx], lastInitial: e.target.value.slice(0, 1) };
                  setStudents(copy);
                }}
              />
            </div>

            <div className="form-field" style={{ display: "flex", alignItems: "end", gap: 8 }}>
              <button
                type="button"
                className="red-button"
                onClick={() => setStudents(students.filter((_, i) => i !== idx))}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
          <button
            type="button"
            className="grey-button"
            onClick={() => setStudents([...(students || []), { firstName: "", lastInitial: "" }])}
          >
            + Add Student
          </button>
          {Array.isArray(students) && students.length > 0 && (
            <button type="button" className="grey-button" onClick={() => setStudents([])}>
              Clear
            </button>
          )}
        </div>

        {createdStudentCreds.length > 0 && (
          <div className="bordered-container" style={{ marginTop: 12 }}>
            <strong>Created Student Accounts</strong>
            <table className="excel-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Name</th>
                  <th>Username (email)</th>
                  <th>Password</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {createdStudentCreds.map((row, i) => (
                  <tr key={i}>
                    <td>{row.label}</td>
                    <td>{row.name}</td>
                    <td>{row.email}</td>
                    <td>{row.password}</td>
                    <td>{new Date(row.createdAt).toLocaleString?.() || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <small className="hint">Share these with the teacher now—passwords aren’t stored in Firestore user docs.</small>
          </div>
        )}
      </div>

      {/* Group: Notes */}
      <div className="field-card">
        <div className="card-title">Notes</div>
        <div className="form-grid">
          <div className="form-field">
            <label>Add Notes</label>
            <textarea
              className="textarea"
              placeholder="Anything helpful to remember?"
              value={newUser.notes}
              onChange={(e) => setNewUser({ ...newUser, notes: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>

    <div className="popup-actions">
      <button onClick={handleAddUser} className="btn-3d btn-green">Save User</button>
      <button onClick={() => setShowAddUserPopup(false)} className="btn-3d btn-red">Cancel</button>
    </div>
  </div>
</div>
)}


      {/* Details Popup */}
      {showDetailsPopup && (
        <div className="fullscreen-modal">
  <div className="fullscreen-content">
  <h2 className="center-text">User Details</h2>
  <div className="modal-content-scroll form-grid">
    <div className="form-field"><label>First Name:</label>{showDetailsPopup.firstName}</div>
    <div className="form-field"><label>Last Name:</label>{showDetailsPopup.lastName}</div>
    <div className="form-field"><label>Email:</label>{showDetailsPopup.email}</div>
    <div className="form-field"><label>School Name:</label>{showDetailsPopup.schoolName}</div>
<div className="form-field"><label>School District:</label>{showDetailsPopup.schoolDistrict}</div>
    <div className="form-field"><label>State:</label>{showDetailsPopup.state}</div>
    <div className="form-field"><label>Grade:</label>{showDetailsPopup.grade}</div>
    <div className="form-field"><label>View:</label>{showDetailsPopup.view}</div>
    <div className="form-field"><label>Role:</label>{showDetailsPopup.role}</div>
    <div className="form-field"><label>Educator Code:</label>{showDetailsPopup.educatorCode}</div>
    <div className="form-field"><label>Notes:</label>{showDetailsPopup.notes}</div>
    <div className="form-field">
      <label>Created:</label>
      {formatDateMaybe(showDetailsPopup.createdAt || showDetailsPopup.joinDate)}
    </div>
    {(showDetailsPopup.role || "").toLowerCase().endsWith("educator") && (
      <>
        <h3 className="center-text" style={{ marginTop: 16 }}>Students</h3>
        <div className="bordered-container">
          {loadingStudents ? (
            <div style={{ padding: 12 }}>Loading students…</div>
          ) : (
            <table className="excel-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Name</th>
                  <th>Username (email)</th>
                  <th>Temp Password</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {studentsOfEducator.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>No students yet.</td>
                  </tr>
                ) : (
                  studentsOfEducator.map((s) => (
                    <tr key={s.id}>
                      <td>{s.label}</td>
                      <td>{s.name}</td>
                      <td>{s.email}</td>
                      <td>{s.tempPassword || "—"}</td>
                      <td>{formatDateMaybe(s.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
          <small className="hint">
            Student password (Educators last name + student first name + student number all lower case and no spaces) or as listed if created here.
          </small>
        </div>
      </>
    )}
  </div>
  <div className="button-group">
    <button onClick={() => setEditUser(showDetailsPopup)} className="grey-button">Edit</button>
    <button onClick={() => handleDeleteUser(showDetailsPopup.id)} className="red-button">Delete</button>
    <button onClick={() => setShowDetailsPopup(null)} className="azul-button">Close</button>
  </div>
</div>

        </div>
      )}

      {/* Edit User Popup */}
      {editUser && (
        <div className="fullscreen-modal">
  <div className="fullscreen-content">
  <h2 className="center-text">Edit User</h2>
<div className="modal-content-scroll form-grid">
  <div className="form-field">
    <label>Sir Name</label>
    <select
      value={editUser.sirName}
      onChange={(e) => setEditUser({ ...editUser, sirName: e.target.value })}
    >
      <option value="">Select Sir Name</option>
      {["N/A", "Mr.", "Mrs.", "Ms.", "Miss", "Principal", "Vice Principal", "Assistant Principal"].map((sirName) => (
        <option key={sirName} value={sirName}>{sirName}</option>
      ))}
    </select>
  </div>

  <div className="form-field">
    <label>First Name</label>
    <input
      type="text"
      value={editUser.firstName}
      onChange={(e) => setEditUser({ ...editUser, firstName: e.target.value })}
    />
  </div>

  <div className="form-field">
    <label>Last Name</label>
    <input
      type="text"
      value={editUser.lastName}
      onChange={(e) => setEditUser({ ...editUser, lastName: e.target.value })}
    />
  </div>

  <div className="form-field">
    <label>Email</label>
    <input
      type="email"
      value={editUser.email}
      onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
    />
  </div>

  <div className="form-field">
    <label>Password</label>
    <input
      type="password"
      value={editUser.password}
      onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
    />
  </div>
{/* INSERT: School fields in Edit modal */}
<div className="form-field">
  <label>School Name</label>
  <input
    type="text"
    value={editUser.schoolName || ""}
    onChange={(e) => setEditUser({ ...editUser, schoolName: e.target.value })}
  />
</div>

<div className="form-field">
  <label>School District</label>
  <input
    type="text"
    value={editUser.schoolDistrict || ""}
    onChange={(e) => setEditUser({ ...editUser, schoolDistrict: e.target.value })}
  />
</div>

  <div className="form-field">
    <label>State</label>
    <select
      value={editUser.state}
      onChange={(e) => setEditUser({ ...editUser, state: e.target.value })}
    >
      <option value="">Select State</option>
      {["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"].map((state) => (
        <option key={state} value={state}>{state}</option>
      ))}
    </select>
  </div>

  <div className="form-field">
    <label>Grade</label>
    <select
      value={editUser.grade}
      onChange={(e) => setEditUser({ ...editUser, grade: e.target.value })}
    >
      <option value="">Select Grade</option>
      {["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th", "14th", "15th", "16th"].map((grade) => (
        <option key={grade} value={grade}>{grade}</option>
      ))}
    </select>
  </div>

  <div className="form-field">
    <label>View</label>
    <select
      value={editUser.view}
      onChange={(e) => setEditUser({ ...editUser, view: e.target.value })}
    >
      <option value="">Select View</option>
      {["Educator", "Student"].map((view) => (
        <option key={view} value={view}>{view}</option>
      ))}
    </select>
  </div>

  <div className="form-field">
    <label>Role</label>
    <select
      value={editUser.role}
      onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
    >
     <option value="">Choose role code</option>
              {(() => {
                const states = [
                  "al","ak","az","ar","ca","co","ct","dc","de","fl","ga",
                  "hi","id","il","in","ia","ks","ky","la","me","md",
                  "ma","mi","mn","ms","mo","mt","ne","nv","nh","nj",
                  "nm","ny","nc","nd","oh","ok","or","pa","ri","sc",
                  "sd","tn","tx","ut","vt","va","wa","wv","wi","wy"
                ];
                const grades = ["1st","2nd","3rd","4th","5th","6th","7th","8th","9th","10th","11th","12th","14th","15th","16th"];
                const roles = ["educator","student"];
                const allRoles = ["administrator"];
                states.forEach(s => grades.forEach(g => roles.forEach(r => allRoles.push(`${s}-${g}-${r}`))));
                return allRoles.map((r) => <option key={r} value={r}>{r}</option>);
              })()}
    </select>
  </div>

  <div className="form-field">
    <label>Educator Code</label>
    <input
      type="text"
      value={editUser.educatorCode}
      onChange={(e) => setEditUser({ ...editUser, educatorCode: e.target.value })}
    />
  </div>

  <div className="form-field">
    <label>Add Notes</label>
    <textarea
      value={editUser.notes}
      onChange={(e) => setEditUser({ ...editUser, notes: e.target.value })}
    />
  </div>
</div>

<div className="button-group">
  <button onClick={handleEditUser} className="green-button">Save</button>
  <button onClick={() => setEditUser(null)} className="red-button">Cancel</button>
</div>
</div>
        </div>
      )}

      {/* ---------------- DELETION REPORT ---------------- */}
      {deleteReport && (
        <div className="fullscreen-modal">
          <div className="abt-report">
            <h2>
              {deleteReport.dryRun
                ? "Deletion preview — nothing has been deleted"
                : "Deletion report"}
            </h2>

            <div className="abt-report-summary">
              <div>
                <strong>{deleteReport.requested}</strong> selected
              </div>
              <div>
                <strong>{deleteReport.processed}</strong> accounts in scope
                {deleteReport.processed > deleteReport.requested &&
                  " (students pulled in)"}
              </div>
              <div>
                <strong>
                  {deleteReport.results.filter((r) => r.backedUp).length}
                </strong>{" "}
                educators backed up
              </div>
              <div>
                <strong>
                  {deleteReport.results.filter((r) => r.status === "ERROR").length}
                </strong>{" "}
                errors
              </div>
            </div>

            {deleteReport.skipped?.length > 0 && (
              <div className="abt-warning">
                Skipped {deleteReport.skipped.length} account(s):{" "}
                {deleteReport.skipped
                  .map((s) => `${s.uid} (${s.reason})`)
                  .join(", ")}
              </div>
            )}

            <div className="abt-report-scroll">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Auth</th>
                    <th>Firestore docs</th>
                    <th>Storage files</th>
                    <th>Backed up</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {deleteReport.results.map((r) => (
                    <tr key={r.uid}>
                      <td>{r.email || r.uid}</td>
                      <td>{r.role || "—"}</td>
                      <td>{r.authFound ? (r.authDeleted ? "deleted" : "found") : "none"}</td>
                      <td title={r.firestoreDocsFound.join("\n")}>
                        {deleteReport.dryRun
                          ? r.firestoreDocsFound.length
                          : r.firestoreDocsDeleted}
                      </td>
                      <td>{r.storageFilesDeleted}</td>
                      <td>{r.backedUp ? "yes" : "—"}</td>
                      <td className={r.status === "ERROR" ? "abt-status-error" : ""}>
                        {r.status}
                        {r.error ? ` — ${r.error}` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="abt-report-actions">
              {deleteReport.dryRun && (
                <button
                  className="abt-btn abt-btn-red"
                  onClick={() => {
                    setDeleteReport(null);
                    handleBulkDeleteEverywhere();
                  }}
                >
                  Proceed with deletion
                </button>
              )}
              <button
                className="abt-btn"
                onClick={() =>
                  downloadCsvFile(
                    `deletion-report-${Date.now()}.csv`,
                    deleteReport.results.map((r) => ({
                      email: r.email,
                      uid: r.uid,
                      role: r.role,
                      authFound: r.authFound,
                      authDeleted: r.authDeleted,
                      firestoreDocs: r.firestoreDocsFound.join(" | "),
                      storageFiles: r.storageFilesDeleted,
                      backedUp: r.backedUp,
                      status: r.status,
                      error: r.error,
                    })),
                    [
                      "email", "uid", "role", "authFound", "authDeleted",
                      "firestoreDocs", "storageFiles", "backedUp", "status", "error",
                    ]
                  )
                }
              >
                Download report CSV
              </button>
              <button className="abt-btn" onClick={() => setDeleteReport(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoles;
